import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { MessageSquarePlus, Bug, Lightbulb, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeedbackDialog = ({ open, onOpenChange }) => {
  const [type, setType] = useState('problem');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const pickScreenshot = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setScreenshot(file);
    setPreview(URL.createObjectURL(file));
  };

  const clearScreenshot = () => {
    if (preview) URL.revokeObjectURL(preview);
    setScreenshot(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const reset = () => {
    setType('problem');
    setMessage('');
    clearScreenshot();
  };

  const submit = async () => {
    if (!message.trim()) {
      toast.error('Please describe your feedback');
      return;
    }
    setSubmitting(true);
    try {
      let screenshot_url = null;
      if (screenshot) {
        const fd = new FormData();
        fd.append('file', screenshot);
        const up = await axios.post(`${API}/feedback/screenshot`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        screenshot_url = up.data.url;
      }
      await axios.post(`${API}/feedback`, {
        type,
        message: message.trim(),
        screenshot_url: screenshot_url || undefined,
      });
      toast.success('Thanks! Your feedback has been sent to the team.');
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const typeBtn = (value, Icon, label, desc) => (
    <button
      type="button"
      onClick={() => setType(value)}
      aria-pressed={type === value}
      className={`flex-1 rounded-lg border-2 p-3 text-left transition-colors ${
        type === value
          ? value === 'problem'
            ? 'border-[#b91c1c] bg-red-50'
            : 'border-[#34657f] bg-[#34657f]/5'
          : 'border-slate-200 hover:border-slate-300'
      }`}
      data-testid={`feedback-type-${value}`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${value === 'problem' ? 'text-[#b91c1c]' : 'text-[#34657f]'}`} />
        <span className="font-semibold text-sm text-[#1A1A1A]">{label}</span>
      </div>
      <p className="text-xs text-[#5C5C5C] mt-1">{desc}</p>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
        <DialogContent className="sm:max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-[#34657f]" />
              Send feedback
            </DialogTitle>
            <DialogDescription>Spotted a problem or have an idea? We read every submission.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="flex gap-3" role="group" aria-label="Feedback type">
              {typeBtn('problem', Bug, 'Report a problem', 'Something is broken or not working right')}
              {typeBtn('suggestion', Lightbulb, 'Suggest an improvement', 'An idea to make LIFE Link better')}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="feedback-message">What would you like to tell us? *</Label>
              <Textarea
                id="feedback-message"
                rows={4}
                placeholder={type === 'problem'
                  ? 'Describe what happened, what you expected, and where it occurred…'
                  : 'Describe your idea and how it would help…'}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={5000}
                data-testid="feedback-message-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="feedback-screenshot">Screenshot (optional — but it helps a lot!)</Label>
              {preview ? (
                <div className="relative inline-block">
                  <img src={preview} alt="Screenshot preview" className="max-h-32 rounded-md border border-slate-200" />
                  <button
                    type="button"
                    onClick={clearScreenshot}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-700 text-white flex items-center justify-center hover:bg-slate-900"
                    aria-label="Remove screenshot"
                    data-testid="feedback-remove-screenshot"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="feedback-screenshot"
                  className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 text-sm text-[#5C5C5C] cursor-pointer hover:border-[#34657f] transition-colors"
                >
                  <ImagePlus className="w-4 h-4 text-[#34657f]" />
                  Attach a screen grab of the issue or idea
                </label>
              )}
              <Input
                ref={fileRef}
                id="feedback-screenshot"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => pickScreenshot(e.target.files?.[0])}
                data-testid="feedback-screenshot-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="feedback-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[#34657f] hover:bg-[#2a5060]" data-testid="feedback-submit-btn">
              {submitting ? 'Sending…' : 'Send feedback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default FeedbackDialog;
